"use client";

/**
 * Unified wishlist hook — handles BOTH backends transparently:
 *   - signed-in user → Supabase `wishlist_items` table
 *   - anonymous       → localStorage `ss-wishlist` array of stringified ids
 *
 * On sign-in, runs a one-time merge: any localStorage ids get upserted into
 * the user's DB wishlist, then localStorage is cleared. This means a guest
 * who saves a few units then signs up keeps them.
 *
 * Returns: { ids, isMember, toggle, isReady }
 *   - ids:       Set<number> of wp_post_id (uniform shape regardless of source)
 *   - isMember:  (id) => bool
 *   - toggle:    (id, slug?) => Promise<void>  — writes to whichever backend
 *   - isReady:   bool — false until the initial load finishes (prevents
 *                rendering the wrong "♡" / "♥" state on first paint)
 */

import { useCallback, useEffect, useState } from "react";
import { createSupabaseBrowserClient } from "./client";
import { addToWishlist, removeFromWishlist, mergeLocalWishlist } from "@/app/actions/wishlist";

const LS_KEY = "ss-wishlist";

function readLocalIds(): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((v) => (typeof v === "number" ? v : parseInt(String(v), 10)))
      .filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
}

function writeLocalIds(ids: number[]): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(LS_KEY, JSON.stringify(ids));
  window.dispatchEvent(new Event("ss:wishlist"));
}

export function useWishlist() {
  const [ids, setIds] = useState<Set<number>>(new Set());
  const [isReady, setIsReady] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Initial load + subscribe to auth changes.
  useEffect(() => {
    const hasSupabase =
      !!process.env.NEXT_PUBLIC_SUPABASE_URL &&
      !!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!hasSupabase) {
      setIds(new Set(readLocalIds()));
      setIsReady(true);
      return;
    }

    const supabase = createSupabaseBrowserClient();
    let cancelled = false;

    async function loadForUser(uid: string) {
      // Merge any pending localStorage wishlist into the DB.
      const local = readLocalIds();
      if (local.length > 0) {
        await mergeLocalWishlist(local);
        writeLocalIds([]); // clear after merge
      }
      const { data } = await supabase
        .from("wishlist_items")
        .select("listing_wp_id")
        .eq("user_id", uid)
        .returns<{ listing_wp_id: number }[]>();
      if (cancelled) return;
      setIds(new Set((data ?? []).map((r) => r.listing_wp_id)));
      setIsReady(true);
    }

    async function loadAnon() {
      if (cancelled) return;
      setIds(new Set(readLocalIds()));
      setIsReady(true);
    }

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (cancelled) return;
      if (user) {
        setUserId(user.id);
        loadForUser(user.id);
      } else {
        loadAnon();
      }
    });

    // React to sign-in / sign-out in another tab or via UserDropdown.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session?.user) {
        setUserId(session.user.id);
        setIsReady(false);
        loadForUser(session.user.id);
      } else {
        setUserId(null);
        setIsReady(false);
        loadAnon();
      }
    });

    // React to localStorage changes from other tabs (anonymous mode).
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY && !userId) {
        setIds(new Set(readLocalIds()));
      }
    };
    const onCustom = () => {
      if (!userId) setIds(new Set(readLocalIds()));
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("ss:wishlist", onCustom);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ss:wishlist", onCustom);
    };
  }, [userId]);

  const isMember = useCallback((id: number) => ids.has(id), [ids]);

  const toggle = useCallback(
    async (id: number, slug?: string | null) => {
      const next = new Set(ids);
      const member = next.has(id);
      if (member) next.delete(id);
      else next.add(id);
      // Optimistic UI: flip immediately, sync in background.
      setIds(next);

      if (userId) {
        const r = member
          ? await removeFromWishlist(id)
          : await addToWishlist(id, slug ?? null);
        if (!r.ok) {
          // Revert on error
          const reverted = new Set(ids);
          setIds(reverted);
        }
      } else {
        writeLocalIds(Array.from(next));
      }
    },
    [ids, userId],
  );

  return { ids, isMember, toggle, isReady };
}
